import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { assign } from 'lodash'
import { Card, Button } from '@material-ui/core'
import FormItem from 'components/FormItem'
import { isBlank } from 'common/usual'
import {
  translationJsonpathDetailPromise,
  translationJsonpathCreatePromise,
  translationJsonpathUpdatePromise,
} from 'api/TranslationJsonpath'

// Paths run against `{ json: <response data> }`, the way the localize middleware wraps a body, so
// they start `$.json....` and not `$....`.
const PATH_PLACEHOLDER = '["$.json.rows..title"]'

export default function TranslationJsonpathUpsert (props) {
  const id = props.match.params.id
  const [row, setRow] = useState({})
  const [btnStatus, setBtnStatus] = useState(false)
  const [error, setError] = useState(null)

  const disableBtn = useMemo(() => (
    btnStatus || isBlank(row.name) || isBlank(row.jsonpathStr)
  ), [btnStatus, row.name, row.jsonpathStr])

  const getDetail = useCallback(async () => {
    if (!id) return
    setRow(await translationJsonpathDetailPromise(id))
  }, [id])

  useEffect(() => { getDetail() }, [getDetail])

  const handleChange = useCallback((...values) => {
    setRow(current => assign({}, current, ...values))
  }, [])

  const commonProps = useMemo(() => ({
    onChange: handleChange,
    data: row
  }), [handleChange, row])

  function handleSubmit () {
    setBtnStatus(true)
    setError(null)

    const body = {
      name: row.name,
      jsonpathStr: row.jsonpathStr,
      avgRating: isBlank(row.avgRating) ? null : Number(row.avgRating),
    }

    const request = id
      ? translationJsonpathUpdatePromise(id, body)
      : translationJsonpathCreatePromise(body)

    request
      .then(() => props.history.goBack())
      // The API parses every path before writing the row, so a 422 here is a malformed path list
      // rather than a server problem -- show it instead of dropping the admin back to the list.
      .catch(err => setError((err && err.message) || 'save failed'))
      .finally(() => setBtnStatus(false))
  }

  return (
    <div className={'root'}>
      <Card className={'form-container'}>
        <FormItem
          label={'Route'}
          name={'name'}
          isRequired
          placeholder={'get-/api/v1/social-feed/posts'}
          title={'method-route, lower case. Stored lower-cased because the lookup lower-cases before reading the cache.'}
          {...commonProps} />
        <FormItem
          label={'JSON Paths'}
          name={'jsonpathStr'}
          isRequired
          multiline
          placeholder={PATH_PLACEHOLDER}
          title={'A JSON array of jsonpath strings. They run against { json: <response data> }, so they start $.json.'}
          {...commonProps} />
        <FormItem label={'Avg Rating'} name={'avgRating'} type={'number'} {...commonProps} />

        { error && <p className='tips' style={{ color: '#b00020' }}>{error}</p> }

        <p className='tips'>
          Ask what the field's value set is before saving. A path that lands on a rendered template
          ("Awarded for completing 12250 workouts"), a count, or member-written text mints a new
          Lokalise key for every distinct value it ever sees. Translate the template, not the
          rendered string.
        </p>

        <Button
          className='save-btn'
          color='primary'
          fullWidth
          size='large'
          type='submit'
          variant='contained'
          onClick={handleSubmit}
          disabled={disableBtn}>
          Save
        </Button>
      </Card>
    </div>
  )
}
